import { Autocomplete, TextField, createFilterOptions } from "@mui/material";
import { FC, KeyboardEvent, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { RiskColumn } from "./riskColumns";
import { joinMultiValue, splitMultiValue } from "./riskEditorState";

interface Props {
	column: RiskColumn;
	value: string;
	options: string[];
	getLabel?: (value: string) => string;
	getSearchText?: (value: string) => string;
	renderOptionContent?: (value: string) => ReactNode;
	emptyText: string;
	onCommit: (value: string) => void;
	onCancel: () => void;
}

const RiskCellEditor: FC<Props> = ({ column, value, options, getLabel, getSearchText, renderOptionContent, emptyText, onCommit,
		onCancel }) => {
	const [draft, setDraft] = useState(value);
	const done = useRef(false);
	const latestDraft = useRef(draft);
	const latestCommit = useRef(onCommit);
	latestDraft.current = draft;
	latestCommit.current = onCommit;
	const freeSolo = column.kind === 'suggest';
	const filterOptions = useMemo(() => createFilterOptions<string>({
		stringify: (option) => getSearchText ? getSearchText(option) : getLabel ? getLabel(option) : option
	}), [getSearchText, getLabel]);

	useEffect(() => () => {
		if (!done.current) {
			done.current = true;
			latestCommit.current(latestDraft.current);
		}
	}, []);

	if (column.kind === 'text') {
		return (
			<TextField
				autoFocus
				fullWidth
				size="small"
				multiline
				maxRows={6}
				value={draft}
				onChange={(event) => setDraft(event.target.value)}
				onBlur={() => commit(draft)}
				onKeyDown={(event) => {
					handleEscape(event);
					if (event.key === 'Enter' && !event.shiftKey) {
						event.preventDefault();
						commit(draft);
					}
				}}
			/>
		);
	}
	if (column.multiple) {
		return (
			<Autocomplete
				multiple
				fullWidth
				size="small"
				openOnFocus
				disableCloseOnSelect
				options={options}
				value={splitMultiValue(draft)}
				getOptionLabel={(option) => getLabel ? getLabel(option) : option}
				filterOptions={filterOptions}
				renderOption={(props, option) => (
					<li {...props} key={option}>{renderOptionContent ? renderOptionContent(option) : getLabel ? getLabel(option) : option}</li>
				)}
				noOptionsText={emptyText}
				onChange={(_, next) => setDraft(joinMultiValue(next))}
				onBlur={() => commit(draft)}
				renderInput={(params) => <TextField {...params} autoFocus onKeyDown={handleEscape}/>}
			/>
		);
	}
	return (
		<Autocomplete
			fullWidth
			size="small"
			openOnFocus
			autoHighlight={!freeSolo}
			freeSolo={freeSolo}
			options={options}
			value={(freeSolo ? value : draft) || null}
			inputValue={freeSolo ? draft : undefined}
			getOptionLabel={(option) => getLabel ? getLabel(option) : option}
			filterOptions={filterOptions}
			renderOption={(props, option) => (
				<li {...props} key={option}>{renderOptionContent ? renderOptionContent(option) : getLabel ? getLabel(option) : option}</li>
			)}
			noOptionsText={emptyText}
			onChange={(_, next, reason) => {
				if (reason === 'clear') {
					setDraft('');
					return;
				}
				commit(next || '');
			}}
			onInputChange={freeSolo ? (_, next, reason) => {
				if (reason !== 'reset') {
					setDraft(next);
				}
			} : undefined}
			onBlur={() => commit(draft)}
			renderInput={(params) => <TextField {...params} autoFocus onKeyDown={handleEscape}/>}
		/>
	);

	function commit(next: string) {
		if (done.current) {
			return;
		}
		done.current = true;
		onCommit(next);
	}

	function cancel() {
		if (done.current) {
			return;
		}
		done.current = true;
		onCancel();
	}

	function handleEscape(event: KeyboardEvent) {
		if (event.key === 'Escape') {
			event.preventDefault();
			event.stopPropagation();
			cancel();
		}
	}
};

export default RiskCellEditor;
